import type { PostHog, SurveyCallback } from "posthog-js";

import type { UnknownRecord } from "@/utils/type";

import type { LicenseInfo, UserInfo } from "../types";

let instance: PostHog | undefined;
let initialization = 0;

interface PosthogConfig {
  apiKey: string;
  reverseProxy: string;
}

export async function initPosthog(config: PosthogConfig, license: LicenseInfo, user: UserInfo) {
  const currentInitialization = ++initialization;
  const posthogInstance = (await import("posthog-js")).default;
  if (currentInitialization !== initialization) return false;
  instance = posthogInstance;

  instance.init(config.apiKey, {
    api_host: config.reverseProxy || "https://eu.i.posthog.com",
    ui_host: "https://eu.posthog.com",
    defaults: "2025-11-30", // Specifies which config to use
    cross_subdomain_cookie: true, // Share session across subdomains (setup.* <-> app.*)
    disable_session_recording: true,
    session_recording: {
      recordCrossOriginIframes: true,
    },
    // Remove IP address
    before_send: (event) => {
      delete event?.properties.$ip;
      return event;
    },
  });
  identifyUser(license, user);
  return true;
}

export function startPosthogSessionRecording() {
  instance?.startSessionRecording({ url_trigger: true });
}

/**
 * Identify a logged-in user with posthog, but only send a limited data set.
 */
function identifyUser(license: LicenseInfo, user: UserInfo) {
  const { company, role } = user;

  instance?.identify(user.id, { company, role, type: license.type, plan: license.plan });

  // Set the company group (allows tracking by company)
  instance?.group("company", company, { id: company });
}

/**
 * Resets the posthog user (so future events aren't linked to the previous user)
 * Should be called on logout
 */
export function logoutPosthogUser() {
  initialization++;
  instance?.reset();
  instance = undefined;
}

export function getPosthogSurveys(callback: SurveyCallback, forceReload = false) {
  instance!.getSurveys(callback, forceReload);
}

export function capturePosthog(event: string, properties: UnknownRecord) {
  instance!.capture(event, properties);
}
